<!--
  StorefrontFooter.vue

  Shopper-facing storefront footer. Shows "Powered by ThreadFlow" plus the
  favicon, and -- when the store has them configured -- Privacy and Refund
  policy links from the store's Tax & Legal settings (campaign
  privacy_policy_* / refund_policy_*, threaded through getStorefrontCampaign
  and resolved by utils/storefrontPolicyLinks).

  Rules (Jake, 2026-07-21; revised 2026-10-19):
   - Link text is the policy's LABEL (default "Privacy Policy" / "Refund
     Policy"). In URL mode the link goes to the store's own URL and shows
     ONLY when it passes the http/https sanitizer; in page mode it goes to
     the hosted page on the store and shows only when the page has content.
     When neither shows, the footer looks exactly as it did before this
     feature (no empty links, no dangling separators).
   - EVERY link in the footer opens in a new tab with rel="noopener
     noreferrer" (reverse-tabnabbing guard, CWE-1022): outside URLs, the
     hosted policy pages and the brand mark. A shopper reads a policy without
     losing the store page or a cart in progress (Bug Intake #457).
   - A middot separator sits between adjacent items only.

  SECURITY: the URLs are distributor-authored. They are bound as an anchor
  `href` only (never v-html) and sanitized to http/https via
  sanitizeExternalUrl, so a stored `javascript:`/`data:` URL cannot become a
  clickable link (XSS boundary). Labels are bound as text.

  WHITE-LABEL (card https://trello.com/c/mq26K91V, Unit 3):

  The brand mark is no longer a constant. When the store's distributor is
  on a tier carrying the white-label entitlement AND the store's own
  toggle is on, the mark becomes the store's own branding and the
  threadflow.com anchor DROPS -- a distributor's own logo linking to
  ThreadFlow's marketing site is precisely the leak the feature exists to
  prevent.

  REVISED 2026-09-20 (Unit 8): a white-labeled store with NO logo now
  renders its plain-text attribution and NO icon at all. It used to fall
  back to the ThreadFlow logo; Jake replaced that rule -- "If the toggle
  is on, no ThreadFlow logo at all." The mark is also a SEQUENCE now
  (text around a {logo} placeholder), rendered by WhiteLabelMark.vue, and
  the logo is height-capped at 64px instead of forced into a 25px square.

  The rule itself lives in `@/utils/whiteLabelMark`, shared with
  StorefrontCart so the two surfaces cannot disagree.
  This component only renders what the rule returns; see that file for the
  full table and its rationale.

  The `whiteLabel` prop is OPTIONAL and its absence renders exactly
  today's footer, so a page that has not been rewired is not broken -- it
  is un-white-labeled, which is the correct default and indistinguishable
  from a store on a tier without the entitlement.

  SECURITY: `attribution` is distributor-authored FREE TEXT. It is bound
  with `{{ }}` (text), never v-html, and is never used as an href. The
  only href this footer can emit for the brand mark is the hard-coded
  ThreadFlow constant.
-->
<template>
  <v-footer class="ow-footer sf-footer" data-testid="storefront-footer">
   <!-- The footer is its own band (top rule, padding) and its content is one
        centered row, matching the centered page content above it (Bug Intake
        #459). The row wraps on a narrow screen rather than overflowing. -->
   <div class="sf-footer__inner" data-testid="storefront-footer-inner">
    <!-- One mark, rendered by the shared component. The anchor, the
         segment order and the 64px height cap all travel with the
         resolved object -- see WhiteLabelMark.vue. The brand-link testid
         is kept on a wrapper so the existing scenarios that assert on it
         still address the same thing. -->
    <span
      v-if="brandMark.isLink"
      class="sf-footer__brand"
      data-testid="storefront-footer-brand-link-wrap"
    >
      <WhiteLabelMark
        :mark="brandMark"
        testid="storefront-footer-brand-link"
        logo-testid="storefront-footer-brand-logo"
      />
    </span>
    <WhiteLabelMark
      v-else
      :mark="brandMark"
      testid="storefront-footer-brand"
      logo-testid="storefront-footer-brand-logo"
    />

    <!--
      Policy links (Privacy / Refund). One loop, because the two are the same
      thing: link text is the policy's label, the target is either the store's
      own URL (opens in a new tab) or the hosted policy page on this store
      (in-app navigation). `policyLinks` holds only policies that HAVE a
      target, so a store with none renders exactly the bare footer.
    -->
    <template v-for="link in policyLinks" :key="link.kind">
      <span
        class="sf-footer__sep"
        data-testid="storefront-footer-legal-sep"
        aria-hidden="true"
        >&middot;</span
      >
      <router-link
        v-if="!link.external"
        class="sf-footer__link"
        :to="link.to"
        target="_blank"
        rel="noopener noreferrer"
        :data-testid="`storefront-footer-${link.kind}-link`"
        >{{ link.label }}</router-link
      >
      <a
        v-else
        class="sf-footer__link"
        :href="link.href"
        target="_blank"
        rel="noopener noreferrer"
        :data-testid="`storefront-footer-${link.kind}-link`"
        >{{ link.label }}</a
      >
    </template>
   </div>
  </v-footer>
</template>

<script>
import { POLICY_KINDS, resolvePolicyLink } from "@/utils/storefrontPolicyLinks";
import { resolveWhiteLabelMark } from "@/utils/whiteLabelMark";
import WhiteLabelMark from "@/components/WhiteLabelMark.vue";

export default {
  name: "StorefrontFooter",
  components: { WhiteLabelMark },
  props: {
    // The store's policies, `{privacy, refund}`, each
    // `{label, mode, url, hasPage}` (see utils/storefrontPolicyLinks and
    // useStorefrontChrome). A policy with no target renders no link.
    policies: {
      type: Object,
      default: null,
    },
    // The store slug, for the in-store route of a hosted policy page.
    storeSlug: {
      type: String,
      default: "",
    },
    // The `white_label` object off the storefront campaign payload:
    // { enabled, logo_key, attribution }. Null/absent renders today's
    // ThreadFlow mark -- see the header.
    whiteLabel: {
      type: Object,
      default: null,
    },
  },
  computed: {
    policyLinks() {
      if (!this.policies) return [];
      return POLICY_KINDS.map((kind) => {
        const link = resolvePolicyLink(this.policies[kind], kind, this.storeSlug);
        return link ? { kind, ...link } : null;
      }).filter(Boolean);
    },
    // The whole white-label decision, made in one shared place. This
    // component deliberately holds NO branch of its own on `enabled` or
    // on `logo_key`: a second copy of the rule here is how this footer
    // and the cart footer would drift.
    brandMark() {
      return resolveWhiteLabelMark(this.whiteLabel);
    },
  },
};
</script>

<style scoped>
.sf-footer {
  display: flex;
  justify-content: center;
  padding: 20px 16px;
  border-top: 1px solid var(--tf-border, #e0e0e0);
  background: var(--tf-bg-subtle, #f7f7f8);
}
.sf-footer__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px 12px;
}
.sf-footer__brand--link {
    /* The brand mark is a link now (it points at the ThreadFlow marketing
     * site) but it must still READ as the footer's brand text -- no button
     * chrome, no underline, inherits the footer ink. Same treatment the
     * legal links beside it already use. */
    color: inherit;
    text-decoration: none;
    cursor: pointer;
}
.sf-footer__brand {
  display: inline-flex;
  align-items: center;
}
.sf-footer__sep {
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.9375rem;
  user-select: none;
}
.sf-footer__link {
  color: inherit;
  opacity: 0.85;
  font-size: 0.9375rem;
  padding: 4px 2px;
  text-decoration: none;
}
.sf-footer__link:hover {
  opacity: 1;
  text-decoration: underline;
}
</style>
